import React, { createRef, useState } from 'react';
import FloatingBoxContext from './FloatingBoxContext';
import type { Box } from './FloatingBoxContext';
import { useClassNameMapper } from 'react-bootstrap';
import localStyles from './FloatingBoxContext.module.pcss';

export enum FloatingBoxLocation {
  BOTTOM_LEFT = 'bottomLeft',
  BOTTOM_RIGHT = 'bottomRight'
}

interface Props {
  /**
   * The order in which the boxes should be rendered, if not provided the order
   * will be the order in which the boxes were added
   */
  forcedComponentOrder?: string[];
}

/**
 * Floating box context provider. This provider is used to add floating boxes to the
 * bottom left and bottom right of the screen.
 *
 * @author Adam Ayres, Willi Hyde
 */
const FloatingBoxContextProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  forcedComponentOrder = []
}) => {
  const cx = useClassNameMapper(localStyles);
  const [boxes, setBoxes] = useState<Box[]>([]);

  /**
   * Check if a box is in the floating box context.
   * @param id The id of the box to check.
   */
  function hasBox(id: string): boolean {
    return boxes.some((box: Box) => box.id === id);
  }

  /**
   * Add a box to the floating box context.
   * @param id The id of the box to add.
   * @param location The location of the box. Default is BOTTOM_LEFT.
   */
  function addBox(
    id: string,
    location: FloatingBoxLocation = FloatingBoxLocation.BOTTOM_LEFT
  ): void {
    if (!hasBox(id)) {
      setBoxes([...boxes, { id, ref: createRef<HTMLDivElement>(), location }]);
    }
  }

  /**
   * Remove a box from the floating box context.
   * @param id The id of the box to remove.
   */
  function removeBox(id: string): void {
    if (hasBox(id)) {
      setBoxes(boxes.filter((box: Box) => box.id !== id));
    }
  }

  /**
   * Clear all boxes from the floating box context.
   */
  function clearBoxes() {
    if (boxes.length > 0) {
      setBoxes([]);
    }
  }

  /**
   * Get a box reference by id.
   * @param boxId The id of the box to get.
   */
  function getBoxReference(boxId: string): Box | undefined {
    return boxes.find(({ id }) => id === boxId);
  }

  const sortedBoxes = boxes.sort((a, b) => {
    const aIndex = forcedComponentOrder.indexOf(a.id);
    const bIndex = forcedComponentOrder.indexOf(b.id);
    if (aIndex === bIndex) {
      return 0;
    }
    return aIndex > bIndex ? 1 : -1;
  });

  const sortedBottomLeftBoxes = sortedBoxes.filter(
    box => box.location === FloatingBoxLocation.BOTTOM_LEFT
  );
  const sortedBottomRightBoxes = sortedBoxes.filter(
    box => box.location === FloatingBoxLocation.BOTTOM_RIGHT
  );

  return (
    <FloatingBoxContext.Provider
      value={{
        addBox,
        removeBox,
        hasBox,
        clearBoxes,
        getBoxReference
      }}
    >
      {sortedBoxes.length > 0 && (
        <div className={cx('lia-boxes')}>
          {sortedBottomLeftBoxes.length > 0 && (
            <div className={cx('lia-left')}>
              {sortedBottomLeftBoxes.map(({ id, ref }) => (
                <div key={id} ref={ref} className={cx('lia-box')} />
              ))}
            </div>
          )}
          {sortedBottomRightBoxes.length > 0 && (
            <div className={cx('lia-right')}>
              {sortedBottomRightBoxes.map(({ id, ref }) => (
                <div key={id} ref={ref} className={cx('lia-box')} />
              ))}
            </div>
          )}
        </div>
      )}
      {children}
    </FloatingBoxContext.Provider>
  );
};

export default FloatingBoxContextProvider;
